import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, Minus } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ChatbotWidget } from "../components/ChatbotWidget";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useToggle } from "../contexts/ToggleContext";
import { customerApi } from "../lib/customerApi";
import { api, formatINR, formatApiErrorDetail } from "../lib/api";
import { getGuestCart, updateGuestCartQty, removeGuestCartItem } from "../lib/guestStorage";

export default function Cart() {
    const { user } = useCustomerAuth();
    const navigate = useNavigate();
    const couponsOn = useToggle("coupons");
    const checkoutOn = useToggle("checkout");
    const [view, setView] = useState({ items: [], subtotal: 0, discount: 0, grandTotal: 0, couponCode: null });
    const [loading, setLoading] = useState(true);
    const [coupon, setCoupon] = useState("");
    const [busy, setBusy] = useState(false);

    const refreshLogged = async () => {
        const { data } = await customerApi.get("/cart");
        setView(data);
    };

    const refreshGuest = async () => {
        const guest = getGuestCart();
        if (!guest.length) {
            setView({ items: [], subtotal: 0, discount: 0, grandTotal: 0, couponCode: null });
            return;
        }
        const items = [];
        let subtotal = 0;
        for (const g of guest) {
            try {
                const { data } = await api.get(`/products/${g.productId}`).catch(async () => {
                    // Fallback: search by id via list endpoint isn't available; we will fetch by id is not supported.
                    return { data: null };
                });
                const prod = data?.product;
                if (!prod) continue;
                const lineTotal = Number(prod.price) * (g.qty || 1);
                subtotal += lineTotal;
                items.push({
                    id: g.id,
                    productId: prod.id,
                    slug: prod.slug,
                    name: prod.name,
                    image: (prod.images || [""])[0],
                    price: Number(prod.price),
                    compareAtPrice: prod.compareAtPrice ? Number(prod.compareAtPrice) : null,
                    size: g.size,
                    qty: g.qty || 1,
                    lineTotal,
                    inStock: prod.inStock,
                });
            } catch (_e) { /* skip */ }
        }
        // Try local product lookup by id; the /products/{slug} endpoint expects slug not id.
        // For guest cart we stored productId — we still need a name/image. We need a /products endpoint by id.
        // Workaround: query /products?limit=100 and match by id (used only for guest path).
        // (Already handled above only when slug equals id which won't be the case.)
        if (items.length === 0) {
            const ids = guest.map((g) => g.productId);
            const { data } = await api.get("/products", { params: { limit: 100 } });
            const map = new Map(data.items.map((p) => [p.id, p]));
            for (const g of guest) {
                const prod = map.get(g.productId);
                if (!prod) continue;
                const lineTotal = Number(prod.price) * (g.qty || 1);
                subtotal += lineTotal;
                items.push({
                    id: g.id,
                    productId: prod.id,
                    slug: prod.slug,
                    name: prod.name,
                    image: (prod.images || [""])[0],
                    price: Number(prod.price),
                    compareAtPrice: prod.compareAtPrice ? Number(prod.compareAtPrice) : null,
                    size: g.size,
                    qty: g.qty || 1,
                    lineTotal,
                    inStock: prod.inStock,
                });
            }
        }
        setView({ items, subtotal, discount: 0, grandTotal: subtotal, couponCode: null });
    };

    const refresh = async () => {
        setLoading(true);
        try {
            if (user) await refreshLogged();
            else await refreshGuest();
        } catch (_e) {
            setView({ items: [], subtotal: 0, discount: 0, grandTotal: 0, couponCode: null });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        const handler = () => refresh();
        window.addEventListener("sukaanyas:cart-changed", handler);
        return () => window.removeEventListener("sukaanyas:cart-changed", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const updateQty = async (item, qty) => {
        if (qty < 1 || qty > 20) return;
        if (user) {
            const { data } = await customerApi.patch(`/cart/items/${item.id}`, { qty });
            setView(data);
        } else {
            updateGuestCartQty(item.id, qty);
        }
    };

    const removeItem = async (item) => {
        if (user) {
            const { data } = await customerApi.delete(`/cart/items/${item.id}`);
            setView(data);
        } else {
            removeGuestCartItem(item.id);
        }
    };

    const applyCoupon = async () => {
        if (!coupon.trim()) return;
        setBusy(true);
        try {
            if (user) {
                const { data } = await customerApi.post("/cart/coupon", { code: coupon.trim() });
                setView(data);
                setCoupon("");
                toast.success("Coupon applied");
            } else {
                // Stateless validation for guest
                const { data } = await api.post(`/checkout/apply-coupon?subtotal=${view.subtotal}`, { code: coupon.trim() });
                setView({ ...view, couponCode: data.code, discount: data.discount, grandTotal: data.grandTotal });
                setCoupon("");
                toast.success("Coupon validated — sign in to lock it in.");
            }
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Invalid coupon");
        } finally {
            setBusy(false);
        }
    };

    const removeCoupon = async () => {
        if (user) {
            const { data } = await customerApi.delete("/cart/coupon");
            setView(data);
        } else {
            setView({ ...view, couponCode: null, discount: 0, grandTotal: view.subtotal });
        }
    };

    const proceedToCheckout = () => {
        if (!checkoutOn) {
            toast.error("Checkout is currently unavailable.");
            return;
        }
        if (!user) navigate("/login?next=/checkout");
        else navigate("/checkout");
    };

    return (
        <div className="min-h-screen bg-ivory" data-testid="cart-page">
            <Header />
            <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-12">
                <span className="label-eyebrow">Your bag</span>
                <h1 className="mt-3 font-display text-4xl sm:text-5xl text-inkDark tracking-tight">Cart</h1>

                {loading ? (
                    <div className="mt-12 text-inkMuted">Loading…</div>
                ) : view.items.length === 0 ? (
                    <div className="mt-12 bg-beige rounded-2xl p-12 text-center" data-testid="cart-empty">
                        <p className="text-inkMuted">Your bag is empty. Time for some boutique browsing.</p>
                        <Link to="/category/sarees" className="btn-pill mt-6 inline-flex">Shop sarees</Link>
                    </div>
                ) : (
                    <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8 space-y-5" data-testid="cart-items-list">
                            {view.items.map((item) => (
                                <div key={item.id} className="bg-ivory border border-inkDark/5 rounded-2xl p-4 flex gap-4" data-testid={`cart-item-${item.slug}`}>
                                    <Link to={`/product/${item.slug}`} className="w-24 h-32 rounded-xl overflow-hidden bg-beige shrink-0">
                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <Link to={`/product/${item.slug}`} className="font-serif text-lg text-inkDark hover:text-skyblueDeep">
                                            {item.name}
                                        </Link>
                                        {item.size ? <div className="label-eyebrow text-[10px] mt-1">Size {item.size}</div> : null}
                                        <div className="mt-1 text-sm text-inkMuted">{formatINR(item.price)}</div>
                                        <div className="mt-3 flex items-center justify-between">
                                            <div className="inline-flex items-center border border-inkDark/10 rounded-full">
                                                <button onClick={() => updateQty(item, item.qty - 1)} className="p-2 disabled:opacity-30" disabled={item.qty <= 1} data-testid={`cart-qty-dec-${item.slug}`}>
                                                    <Minus size={14} />
                                                </button>
                                                <span className="px-3 text-sm text-inkDark min-w-[2ch] text-center">{item.qty}</span>
                                                <button onClick={() => updateQty(item, item.qty + 1)} className="p-2 disabled:opacity-30" disabled={item.qty >= 20} data-testid={`cart-qty-inc-${item.slug}`}>
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-inkDark font-medium">{formatINR(item.lineTotal)}</span>
                                                <button onClick={() => removeItem(item)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid={`cart-remove-${item.slug}`}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start space-y-5">
                            <div className="bg-beige rounded-2xl p-6" data-testid="cart-summary">
                                <div className="font-display text-2xl text-inkDark">Order summary</div>
                                <dl className="mt-5 space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <dt className="text-inkMuted">Subtotal</dt>
                                        <dd className="text-inkDark">{formatINR(view.subtotal)}</dd>
                                    </div>
                                    {view.discount > 0 ? (
                                        <div className="flex justify-between text-skyblueDeep">
                                            <dt>Discount {view.couponCode ? `(${view.couponCode})` : ""}</dt>
                                            <dd>−{formatINR(view.discount)}</dd>
                                        </div>
                                    ) : null}
                                    <div className="flex justify-between">
                                        <dt className="text-inkMuted">Shipping</dt>
                                        <dd className="text-inkDark">Free</dd>
                                    </div>
                                    <div className="border-t border-inkDark/10 pt-3 flex justify-between font-medium text-base">
                                        <dt className="text-inkDark">Total</dt>
                                        <dd className="text-inkDark" data-testid="cart-grand-total">{formatINR(view.grandTotal)}</dd>
                                    </div>
                                </dl>
                                <button onClick={proceedToCheckout} className="btn-pill w-full justify-center mt-6" data-testid="cart-checkout-button">
                                    Proceed to checkout
                                </button>
                            </div>

                            {couponsOn ? (
                                <div className="bg-ivory border border-inkDark/5 rounded-2xl p-6" data-testid="coupon-section">
                                    <div className="label-eyebrow">Have a coupon?</div>
                                    {view.couponCode ? (
                                        <div className="mt-3 flex items-center justify-between">
                                            <span className="text-sm text-inkDark">Applied: <strong>{view.couponCode}</strong></span>
                                            <button onClick={removeCoupon} className="text-sm text-red-600 hover:underline" data-testid="coupon-remove-button">Remove</button>
                                        </div>
                                    ) : (
                                        <div className="mt-3 flex gap-2">
                                            <input
                                                value={coupon}
                                                onChange={(e) => setCoupon(e.target.value)}
                                                placeholder="WELCOME10"
                                                className="flex-1 rounded-xl border border-inkDark/10 bg-white px-3 py-2 text-sm outline-none focus:border-skyblueDeep"
                                                data-testid="coupon-input"
                                            />
                                            <button onClick={applyCoupon} disabled={busy} className="rounded-full bg-skyblue text-inkDark hover:bg-skyblueDeep hover:text-white px-4 py-2 text-sm transition-colors disabled:opacity-50" data-testid="coupon-apply-button">
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                    <div className="mt-3 text-xs text-inkMuted">Try WELCOME10 (10% off ₹2000+) or FLAT500 (₹500 off ₹3000+).</div>
                                </div>
                            ) : null}
                        </aside>
                    </div>
                )}
            </main>
            <Footer />
            <ChatbotWidget />
        </div>
    );
}
